import { useQuery } from '@tanstack/react-query';

import { bamsShortsApi } from 'api/bamsShorts';

import { BAMS_SHORTS_GC_TIME, BAMS_SHORTS_QUERY_KEY, BAMS_SHORTS_STALE_TIME } from './bamsShortsQueryKeys';

/**
 * BAMS Day & line detail for the current + prior ISO pack week. Fetches the aggregate
 * feed and the SKU grid together (four requests) and returns them as `{ days, dsku }`.
 * Lazy: pass `enabled` true only when the Day & line tab is open.
 *
 * A single week failing is tolerated, but a total failure of either the aggregate feed
 * or the SKU grid throws — so a failed SKU grid surfaces an error instead of looking
 * like an empty (below-threshold) result.
 *
 * @param {Object} params
 * @param {{week: number, year: number}} params.cur
 * @param {{week: number, year: number}} params.prv
 * @param {boolean} [params.enabled=false]
 * @returns {import('@tanstack/react-query').UseQueryResult<{days: Array<Object>, dsku: Array<Object>}, Error>}
 */
export const useBamsShortsDayLineQuery = ({ cur, enabled = false, prv }) =>
    useQuery({
        queryKey: [BAMS_SHORTS_QUERY_KEY, 'dayLine', cur?.year, cur?.week, prv?.year, prv?.week],
        enabled: enabled && !!cur && !!prv,
        staleTime: BAMS_SHORTS_STALE_TIME,
        gcTime: BAMS_SHORTS_GC_TIME,
        queryFn: async () => {
            const [d1, d2, k1, k2] = await Promise.all([
                bamsShortsApi.getDayLine(cur.week, cur.year),
                bamsShortsApi.getDayLine(prv.week, prv.year),
                bamsShortsApi.getDayLineSku(cur.week, cur.year),
                bamsShortsApi.getDayLineSku(prv.week, prv.year),
            ]);
            if (d1?.status === false && d2?.status === false) {
                throw new Error(d1?.msg || d2?.msg || 'Could not load day & line detail.');
            }
            if (k1?.status === false && k2?.status === false) {
                throw new Error(k1?.msg || k2?.msg || 'Could not load day & line SKU detail.');
            }
            return {
                days: [].concat(d1?.data?.rows || [], d2?.data?.rows || []),
                dsku: [].concat(k1?.data?.rows || [], k2?.data?.rows || []),
            };
        },
    });
